import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Sun, Moon, Building2, ArrowRight } from 'lucide-react';
import DeviceCodeStep from '../components/auth/DeviceCodeStep';
import ForgotPasswordStep from '../components/auth/ForgotPasswordStep';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('staff');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [forgot, setForgot] = useState(false);
  
  // Platform branding from context
  const { branding } = useBranding();
  
  const { login, register, API } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (isLogin) {
        const res = await login(email, password, rememberMe);
        if (res?.verification) {
          setChallenge(res.verification);
          setLoading(false);
          return;
        }
      } else {
        await register(email, password, name, role);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Autenticazione fallita');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Pannello Sinistro - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <img 
                src={`${API}${branding.logo_url}`} 
                alt={branding.brand_name || 'Logo'} 
                className="h-12 w-auto object-contain"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
            )}
            {branding.brand_name && (
              <span className="text-xl font-semibold font-outfit">{branding.brand_name}</span>
            )}
            {!branding.brand_name && !branding.logo_url && (
              <span className="text-xl font-semibold font-outfit">Pannello Admin</span>
            )}
          </div>
          
          <div className="space-y-6">
            {(branding.brand_name || branding.logo_url) && (
              <div className="mb-8">
                {branding.logo_url && (
                  <img 
                    src={`${API}${branding.logo_url}`} 
                    alt={branding.brand_name || 'Logo'} 
                    className="h-20 w-auto object-contain mb-4"
                  />
                )}
                {branding.brand_name && (
                  <h2 className="text-2xl font-bold font-outfit">{branding.brand_name}</h2>
                )}
              </div>
            )}
            <h1 className="text-4xl md:text-5xl font-bold font-outfit leading-tight">
              La Tua Attività,<br />Una Piattaforma
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              Gestisci clienti, appuntamenti, messaggi, recensioni e social media — tutto da un'unica dashboard.
            </p>
            
            <div className="flex flex-wrap gap-3 pt-4">
              {['CRM', 'Calendario', 'Messaggi', 'Recensioni', 'Social Media', 'Assistente IA'].map((feature) => (
                <span key={feature} className="px-3 py-1.5 bg-white/10 rounded-full text-sm backdrop-blur-sm">
                  {feature}
                </span>
              ))}
            </div>
          </div>
          
          <p className="text-sm text-white/60">
            Prova gratuita di 14 giorni, senza carta di credito
          </p>
        </div>
        
        {/* Elementi decorativi */}
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-20 -right-10 w-40 h-40 rounded-full bg-white/5" />
      </div>

      {/* Pannello Destro - Form Auth */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <div className="flex justify-between items-center p-6">
          <div className="lg:hidden flex items-center gap-2">
            {branding.logo_url ? (
              <img
                src={`${API}${branding.logo_url}`}
                alt={branding.brand_name || 'Logo'}
                className="h-8 w-8 rounded-lg object-contain"
                data-testid="login-mobile-logo"
              />
            ) : (
              <Building2 className="w-6 h-6 text-primary" />
            )}
            <span className="font-semibold font-outfit">{branding.brand_name || 'Salon Suite'}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md space-y-8">
            {challenge ? (
              <DeviceCodeStep
                challenge={challenge}
                rememberMe={rememberMe}
                onCancel={() => { setChallenge(null); setError(''); }}
              />
            ) : forgot ? (
              <ForgotPasswordStep initialEmail={email} onBack={() => setForgot(false)} />
            ) : (
            <>
            <div className="space-y-2">
              <h2 className="text-2xl md:text-3xl font-bold font-outfit">
                {isLogin ? 'Bentornato' : 'Crea il tuo account'}
              </h2>
              <p className="text-muted-foreground">
                {isLogin 
                  ? 'Inserisci le tue credenziali per accedere alla dashboard' 
                  : `Inizia a gestire la tua attività con ${branding.brand_name || 'Salon Suite'}`}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Completo</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Mario Rossi"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={!isLogin}
                    data-testid="name-input"
                    className="h-11"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@esempio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Inserisci la tua password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="h-11"
                />
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="role">Ruolo</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="h-11" data-testid="role-select">
                      <SelectValue placeholder="Seleziona il tuo ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Amministratore</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Dipendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isLogin && (
                <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="remember-me-label">
                  <Checkbox checked={rememberMe} onCheckedChange={v => setRememberMe(v === true)} data-testid="remember-me-checkbox" />
                  <span className="text-sm text-muted-foreground">Resta connesso su questo dispositivo (30 giorni)</span>
                </label>
              )}
              {error && (
                <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md" data-testid="error-message">
                  {error}
                </p>
              )}

              <Button 
                type="submit" 
                className="w-full h-11" 
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? 'Attendere...' : (isLogin ? 'Accedi' : 'Crea Account')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              {isLogin && (
                <button type="button" onClick={() => { setForgot(true); setError(''); }}
                  className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="forgot-password-link">
                  Password dimenticata?
                </button>
              )}
            </form>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin 
                  ? "Non hai un account? Registrati" 
                  : 'Hai già un account? Accedi'}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
